import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/Button';
import { Skeleton } from 'primereact/skeleton';

import { useAudioMute, useRoutingState } from '../../../queries/hardware';
import FeatureUnavailable from '../../../components/activation/FeatureUnavailable';
import { useEntitlements } from '../../../hooks/useEntitlements';
import { useCustomToast } from '../../Contexts/ToastContext';
import { buildEncoderColorMap, buildScreenSourceMap, unwrapRoutingState } from '../sourceColors';

/**
 * Cards for each screen. Mute toggles call POST /api/hardware/{ptn_id}/
 * audio/mute via the existing useAudioMute hook.
 *
 * Each screen card is colour-coded by the SOURCE (encoder) currently feeding
 * its decoder — read from the live routing-state (useRoutingState). The colour
 * matches the same source's card in SourcesPanel, so you can see at a glance
 * which source is on which screen. `sourceByScreen` (optimistic, from the
 * parent) is kept only as a label fallback before the routing-state refetches.
 */
export default function ScreenGrid({ screens, loading, sourceByScreen, activeChannelId }) {
  const { t } = useTranslation();
  const { has } = useEntitlements();
  const { showToast } = useCustomToast();

  // Live routing → colour map (encoder→colour) + per-screen source (screen→encoder).
  const { data: routingData } = useRoutingState({ enabled: has('hardware_control') });
  const { colorMap, screenSrc, nameMap } = useMemo(() => {
    const { encoders, routes } = unwrapRoutingState(routingData);
    return {
      colorMap: buildEncoderColorMap(encoders),
      screenSrc: buildScreenSourceMap(routes),
      nameMap: Object.fromEntries((encoders || []).map((e) => [e.id, e.name])),
    };
  }, [routingData]);

  // Local mute state — optimistic. Keyed by screen.id.
  const [mutedMap, setMutedMap] = useState({});

  if (!has('hardware_control')) {
    return (
      <div className="remote-section">
        <div className="remote-section-title">
          <i className="pi pi-desktop" /> {t('remote_control.sections.screens')}
        </div>
        <FeatureUnavailable compact />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="remote-section">
        <div className="remote-section-title">
          <i className="pi pi-desktop" /> {t('remote_control.sections.screens')}
        </div>
        <div className="remote-screen-grid">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} height="9rem" />)}
        </div>
      </div>
    );
  }

  if (!screens?.length) {
    return (
      <div className="remote-section">
        <div className="remote-section-title">
          <i className="pi pi-desktop" /> {t('remote_control.sections.screens')}
        </div>
        <p className="text-muted small mb-0">{t('remote_control.screens.empty')}</p>
      </div>
    );
  }

  return (
    <div className="remote-section">
      <div className="remote-section-title">
        <i className="pi pi-desktop" /> {t('remote_control.sections.screens')}
      </div>

      <div className="remote-screen-grid">
        {screens.map((s) => {
          const encoderId = screenSrc[s.id] ?? null;
          return (
            <ScreenCard
              key={s.id}
              screen={s}
              sourceColor={encoderId ? colorMap[encoderId] : null}
              sourceName={encoderId ? nameMap[encoderId] : null}
              source={sourceByScreen?.[s.id] ?? (activeChannelId ? '' : null)}
              muted={!!mutedMap[s.id]}
              onMuteToggle={(next) => setMutedMap((m) => ({ ...m, [s.id]: next }))}
              showToast={showToast}
              t={t}
            />
          );
        })}
      </div>
    </div>
  );
}

/* ── Per-screen card ─────────────────────────────────────────────────── */

function ScreenCard({ screen, sourceColor, sourceName, source, muted, onMuteToggle, showToast, t }) {
  const audioMut = useAudioMute(screen.decoder?.id);

  const hasDecoder = !!screen.decoder?.id;

  const toggleMute = () => {
    if (!hasDecoder) {
      showToast('warn', t('remote_control.title'), t('remote_control.screens.no_decoder'));
      return;
    }
    const next = !muted;
    onMuteToggle(next); // optimistic
    audioMut.mutate(next, {
      onSuccess: () => showToast('success',
        t('remote_control.title'),
        next ? t('remote_control.toasts.muted') : t('remote_control.toasts.unmuted')),
      onError: () => onMuteToggle(!next), // rollback
    });
  };

  // Routed source (from live routing-state) drives the colour; fall back to the
  // optimistic label only until the routing-state refetches.
  const routed = !!sourceColor;
  const isActive = routed || !!source;
  const sourceLabel = sourceName || source || t('remote_control.screens.no_source');

  // Colour the whole card border + a thicker accent edge in the source colour.
  const colorStyle = routed
    ? { borderColor: sourceColor, borderInlineStartWidth: 6, borderInlineStartColor: sourceColor }
    : undefined;

  return (
    <div
      className={`remote-screen-card ${isActive ? 'remote-screen-card-active' : ''}`}
      style={colorStyle}
    >
      <div className="d-flex align-items-center gap-2">
        <span className="remote-screen-icon"><i className="pi pi-desktop" /></span>
        <div className="d-flex flex-column flex-grow-1 min-w-0">
          <span className="remote-screen-name text-truncate">{screen.name}</span>
          {screen.location && <span className="remote-screen-meta">{screen.location}</span>}
        </div>
      </div>

      <div className="remote-screen-meta d-flex align-items-center gap-1">
        {routed && (
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
              background: sourceColor, flex: '0 0 auto',
            }}
          />
        )}
        <strong>{t('remote_control.screens.source')}:</strong>{' '}
        <span className="text-truncate" style={routed ? { color: sourceColor, fontWeight: 600 } : undefined}>
          {sourceLabel}
        </span>
      </div>

      {screen.has_audio_out && (
        <span className={`remote-audio-pill ${muted ? 'remote-audio-off' : 'remote-audio-on'}`}>
          <i className={`pi ${muted ? 'pi-volume-off' : 'pi-volume-up'}`} />
          {t('remote_control.screens.audio')}:{' '}
          {muted ? t('remote_control.screens.audio_off') : t('remote_control.screens.audio_on')}
        </span>
      )}

      <div className="remote-screen-actions">
        <Button
          size="small"
          outlined
          severity={muted ? 'success' : 'secondary'}
          icon={`pi ${muted ? 'pi-volume-up' : 'pi-volume-off'}`}
          label={muted ? t('remote_control.screens.unmute') : t('remote_control.screens.mute')}
          loading={audioMut.isLoading}
          disabled={!hasDecoder}
          onClick={toggleMute}
        />
      </div>
    </div>
  );
}
